import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { fadeInUp, staggerContainer } from "@/motion/variants";
import { TradingPartnersTrustBar } from "@/components/TrustBar";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { TradingTrustStrip } from '@/components/TradingTrustStrip';
import { FeatureBadges } from '@/components/MicroTrustBadges';
import { Ship, TrendingUp, Shield, FileText, CreditCard, MapPin, Calendar, CheckCircle, Building2, FileCheck, Banknote } from "lucide-react";

const AmsulUsa = () => {
  return (
    <div className="min-h-screen pt-6">
      <SEO
        title="Ammonium Sulphate Trading | USA Forward CFR Deals | LC-Backed | Padmora"
        description="Specialized ammonium sulphate trading for US seasonal peaks. Forward CFR deals from FOB suppliers to US destinations with full LC financing, SGS inspections, and ICC(A) marine insurance."
        image="/og-trading.jpg"
        canonical="https://padmora.com/trading/amsul-usa"
        keywords={[
          "ammonium sulphate trading",
          "USA fertilizer imports",
          "forward CFR deals",
          "FOB to CFR conversion",
          "seasonal fertilizer trading",
          "US agricultural commodities",
          "LC-backed fertilizer deals",
          "SGS inspection certificates",
          "marine cargo insurance",
          "commodity finance",
          "fertilizer supply chain",
          "US seasonal peaks"
        ]}
        aiOptimization={{
          aiKeywords: [
            "predictive fertilizer demand analysis",
            "AI seasonal trading optimization",
            "automated commodity logistics",
            "intelligent supply chain forecasting",
            "machine learning market timing"
          ],
          entities: [
            "Ammonium Sulphate",
            "United States",
            "FOB",
            "CFR",
            "Fertilizer Trading",
            "Seasonal Demand",
            "SGS Inspection"
          ],
          topics: [
            "Agricultural Commodities",
            "Fertilizer Trading",
            "Seasonal Markets",
            "Supply Chain Optimization",
            "US Import Markets"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Ammonium Sulphate Trading Service",
          "description": "Specialized ammonium sulphate trading for US seasonal peaks with forward CFR deals and LC financing.",
          "provider": {
            "@type": "Organization",
            "name": "Padmora"
          },
          "serviceType": "Commodity Trading",
          "areaServed": {
            "@type": "Country",
            "name": "United States"
          },
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "Trade Type",
              "value": "Forward CFR"
            },
            {
              "@type": "PropertyValue",
              "name": "Payment Method",
              "value": "Letter of Credit"
            },
            {
              "@type": "PropertyValue",
              "name": "Inspection",
              "value": "SGS Certificate"
            }
          ]
        }}
        breadcrumbs={[
          { name: "Home", url: "https://padmora.com/" },
          { name: "Trading", url: "https://padmora.com/trading" },
          { name: "Ammonium Sulphate → USA", url: "https://padmora.com/trading/amsul-usa" }
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-slate-900 mb-6"
            >
              Forward CFR for US Seasonal Peaks
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-slate-600 mb-8"
            >
              Ammonium Sulphate focus • Secure FOB supply during off-season • Deliver CFR into US peak windows
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Send enquiry
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="/trading"
                  className="text-slate-700 hover:text-slate-900"
                >
                  Check Trading Overview
                </a>
              </Button>
            </motion.div>
          </motion.div>

                    <TradingTrustStrip />

          {/* Partners */}
          <div className="mt-16">
            <TradingPartnersTrustBar />
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12"
            >
              Why Ammonium Sulphate → USA?
            </motion.h2>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-3 gap-8"
            >
              <motion.div
                variants={fadeInUp}
                className="text-center bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Shield className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Secure FOB Supply</h3>
                <p className="text-slate-600">
                  Lock in FOB pricing during off-season when suppliers have excess capacity and competitive pricing
                </p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="text-center bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Ship className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4">CFR Peak Delivery</h3>
                <p className="text-slate-600">
                  Time delivery to coincide with US seasonal demand peaks for maximum market value
                </p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="text-center bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <CreditCard className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4">LC-Backed Security</h3>
                <p className="text-slate-600">
                  Full LC protection with SGS inspection and ICC(A) insurance for complete peace of mind
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12"
            >
              Forward CFR Timeline
            </motion.h2>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-3 gap-8"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-8 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-blue-600">Q3</span>
                  </div>
                  <h3 className="text-xl font-semibold">Off-Season (Jul-Sep)</h3>
                </div>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span>Conditionally secure FOB supply (subject to LC)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span>Scout freight window and shipping capacity</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span>Coordinate SGS inspection scheduling</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-8 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-blue-600">Q4</span>
                  </div>
                  <h3 className="text-xl font-semibold">Activation (Oct-Dec)</h3>
                </div>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span>US buyer issues irrevocable, transferable LC</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span>Transfer/issue sub-LC to FOB supplier</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span>Confirm freight fixture and laycan</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-8 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-blue-600">Q1</span>
                  </div>
                  <h3 className="text-xl font-semibold">Delivery (Jan-Mar)</h3>
                </div>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span>Shipment with SGS inspection</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span>Document presentation to confirming bank</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span>Payment upon CFR delivery to US port</span>
                  </li>
                </ul>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* For Buyers / For Sellers */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto"
          >
            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-lg transition-shadow"
            >
              <h3 className="text-2xl font-bold mb-6 text-slate-900 flex items-center gap-2">
                <TrendingUp className="h-6 w-6" />
                For Buyers (CFR)
              </h3>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Destination Ports</h4>
                  <FeatureBadges
                    features={[
                      { icon: <MapPin className="h-4 w-4" />, label: "Freeport TX" },
                      { icon: <MapPin className="h-4 w-4" />, label: "Tampa FL" },
                      { icon: <MapPin className="h-4 w-4" />, label: "Selected East Coast ports" }
                    ]}
                    align="left"
                    size="sm"
                    className="mt-2"
                  />
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Instrument</h4>
                  <p className="text-slate-600 mb-2">Transferable, confirmed LC (preferred)</p>
                  <p className="text-sm text-slate-500">Back-to-back or SBLC acceptable if transferable not available</p>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">CFR Breakdown Provided</h4>
                  <FeatureBadges
                    features={[
                      { icon: <FileText className="h-4 w-4" />, label: "FOB cost analysis" },
                      { icon: <Ship className="h-4 w-4" />, label: "Freight component" },
                      { icon: <Shield className="h-4 w-4" />, label: "Insurance costs" },
                      { icon: <CheckCircle className="h-4 w-4" />, label: "SGS inspection fees" },
                      { icon: <CreditCard className="h-4 w-4" />, label: "Bank charges" }
                    ]}
                    align="left"
                    size="sm"
                    className="mt-2"
                  />
                </div>
              </div>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-lg transition-shadow"
            >
              <h3 className="text-2xl font-bold mb-6 text-slate-900 flex items-center gap-2">
                <FileText className="h-6 w-6" />
                For Sellers (FOB)
              </h3>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Your Role</h4>
                  <p className="text-slate-600">You ship FOB; we handle CFR logistics and payment security</p>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Payment Terms</h4>
                  <p className="text-slate-600 mb-2">Paid under LC (transfer/sub-LC) upon shipment</p>
                  <p className="text-sm text-slate-500">No advance payments required - bank-to-bank security</p>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Our Coordination</h4>
                  <FeatureBadges
                    features={[
                      { icon: <CheckCircle className="h-4 w-4" />, label: "SGS inspection coordination" },
                      { icon: <Shield className="h-4 w-4" />, label: "Insurance ICC(A) arrangement" },
                      { icon: <Ship className="h-4 w-4" />, label: "Shipbroker fixture coordination" },
                      { icon: <FileText className="h-4 w-4" />, label: "Document preparation" },
                      { icon: <CreditCard className="h-4 w-4" />, label: "LC compliance support" }
                    ]}
                    align="left"
                    size="sm"
                    className="mt-2"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-slate-900 mb-6"
            >
              Ready to Discuss Ammonium Sulphate → USA?
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 mb-8"
            >
              Send enquiry to discuss your specific requirements and explore forward CFR opportunities.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button
                size="lg"
                variant="default"
                className="px-8 py-4 text-lg bg-primary hover:bg-primary/90 text-white"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Send enquiry
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AmsulUsa;
